import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import MetricCard from '../../components/admin/MetricCard';
import SectionCard from '../../components/admin/SectionCard';
import { ErrorState, LoadingState } from '../../components/admin/StateViews';
import StatusChip from '../../components/admin/StatusChip';
import { Cause } from '../../types';
import { CauseService } from '../../services/CauseService';
import { AuditLogService } from '../../services/AuditLogService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatNumber = (value: number | null) =>
  value === null
    ? 'Not set'
    : new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
      }).format(value);

const formatBoolean = (value: boolean) => (value ? 'Yes' : 'No');

const CauseDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canManage = ['SuperAdmin', 'Content-Manager'].includes(user?.role || '');
  const [cause, setCause] = useState<Cause | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCause = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setCause(await CauseService.getCauseById(id));
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load cause details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCause();
  }, [loadCause]);

  const handlePublishToggle = async () => {
    if (!cause) {
      return;
    }

    const nextCause =
      cause.status === 'published'
        ? await CauseService.unpublishCause(cause.id)
        : await CauseService.publishCause(cause.id);

    setCause(nextCause);
    showToast(
      cause.status === 'published'
        ? 'Cause moved back to draft.'
        : 'Cause published successfully.'
    );
    await AuditLogService.recordEntry({
      module: 'causes',
      action: cause.status === 'published' ? 'unpublished' : 'published',
      description: `${cause.title} ${
        cause.status === 'published' ? 'moved back to draft' : 'was published'
      }.`,
      actorEmail: user?.email || 'admin@local',
      targetId: cause.id,
      targetLabel: cause.title,
    });
  };

  if (loading) {
    return <LoadingState label="Loading cause details…" />;
  }

  if (error || !cause) {
    return (
      <ErrorState
        message={error || 'Cause not found.'}
        onRetry={() => {
          loadCause();
        }}
      />
    );
  }

  return (
    <AdminPage
      title={cause.title}
      description={cause.shortDescription || cause.description}
      action={
        <Stack direction="row" spacing={1.5}>
          {canManage ? (
            <>
              <Button variant="outlined" onClick={handlePublishToggle}>
                {cause.status === 'published' ? 'Move to draft' : 'Publish'}
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate(`/causes/${cause.id}/edit`)}
              >
                Edit Cause
              </Button>
            </>
          ) : null}
        </Stack>
      }
    >
      {cause.status === 'archived' ? (
        <Alert severity="warning">
          This cause is archived and should not be promoted until it is reviewed.
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Goal amount"
            value={formatCurrency(cause.goalAmount)}
            helperText="Target contribution amount"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Raised amount"
            value={formatCurrency(cause.currentAmount)}
            helperText="Current amount synced from donations"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Source ID"
            value={cause.externalProgramId || cause.id}
            helperText="Unique program identifier"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Rank"
            value={cause.rank === null ? 'Not set' : String(cause.rank)}
            helperText={cause.organizer || 'No organizer assigned'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Cause summary"
            description="Core information and publishing metadata."
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatusChip value={cause.status} />
                {cause.featured ? <StatusChip value="featured" /> : null}
                {cause.shareable ? <StatusChip value="shareable" /> : null}
                {cause.published ? <StatusChip value="published" /> : null}
                {cause.category ? <StatusChip value={cause.category} /> : null}
              </Stack>
              <Typography color="text.secondary">{cause.description}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Source program ID
                  </Typography>
                  <Typography>{cause.externalProgramId || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Rank
                  </Typography>
                  <Typography>{cause.rank ?? 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Slug
                  </Typography>
                  <Typography>{cause.slug || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography>{cause.location || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Start date
                  </Typography>
                  <Typography>{cause.startDate || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    End date
                  </Typography>
                  <Typography>{cause.endDate || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Meal type
                  </Typography>
                  <Typography>{cause.mealType || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Special type
                  </Typography>
                  <Typography>{cause.specialType || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Tags
                  </Typography>
                  <Typography>{cause.tags.join(', ') || 'No tags'}</Typography>
                </Grid>
              </Grid>
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Media"
            description="Primary cover and gallery preview."
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 10',
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: 'action.hover',
                  backgroundImage: cause.coverImageUrl
                    ? `url(${cause.coverImageUrl})`
                    : cause.images[0]?.url
                      ? `url(${cause.images[0].url})`
                      : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Grid container spacing={1.5}>
                {cause.images.map((image) => (
                  <Grid item xs={4} key={image.id}>
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: 2,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'action.hover',
                        backgroundImage: `url(${image.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard
        title="SEO"
        description="Metadata fields available for future search and sharing integrations."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              SEO title
            </Typography>
            <Typography>{cause.seoTitle || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              SEO description
            </Typography>
            <Typography>{cause.seoDescription || 'Not set'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Localized Content"
        description="Program titles and long-form descriptions across supported languages."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Title (English)
            </Typography>
            <Typography>{cause.titleEn || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Title (Arabic)
            </Typography>
            <Typography>{cause.titleAr || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description title
            </Typography>
            <Typography>{cause.descriptionTitle || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description title (English)
            </Typography>
            <Typography>{cause.descriptionTitleEn || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description title (Arabic)
            </Typography>
            <Typography>{cause.descriptionTitleAr || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description details
            </Typography>
            <Typography>{cause.descriptionDetails || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description details (English)
            </Typography>
            <Typography>{cause.descriptionDetailsEn || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Description details (Arabic)
            </Typography>
            <Typography>{cause.descriptionDetailsAr || 'Not set'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Funding & Impact"
        description="Detailed commercial, fundraising, and impact metrics stored for the program."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Goal amount (USD)
            </Typography>
            <Typography>{formatNumber(cause.goalAmountUsd)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Goal amount (EGP)
            </Typography>
            <Typography>{formatNumber(cause.goalAmountEgp)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Raised amount (USD)
            </Typography>
            <Typography>{formatNumber(cause.raisedAmountUsd)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Raised amount (EGP)
            </Typography>
            <Typography>{formatNumber(cause.raisedAmountEgp)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Meal price (USD)
            </Typography>
            <Typography>{formatNumber(cause.mealPriceUsd)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Meal price (EGP)
            </Typography>
            <Typography>{formatNumber(cause.mealPriceEgp)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Minimum amount (USD)
            </Typography>
            <Typography>{formatNumber(cause.minAmountUsd)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Minimum amount (EGP)
            </Typography>
            <Typography>{formatNumber(cause.minAmountEgp)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Raised percentage
            </Typography>
            <Typography>{formatNumber(cause.raisedPercentage)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Families helped
            </Typography>
            <Typography>{formatNumber(cause.familiesHelped)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Meals distributed
            </Typography>
            <Typography>{formatNumber(cause.mealsDistributed)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Number of donors
            </Typography>
            <Typography>{formatNumber(cause.numberDonors)}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Messaging & Sharing"
        description="Campaign messaging, share text, and donor communication content."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Message
            </Typography>
            <Typography>{cause.message || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Message (English)
            </Typography>
            <Typography>{cause.messageEn || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Message (Arabic)
            </Typography>
            <Typography>{cause.messageAr || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Share text
            </Typography>
            <Typography>{cause.shareText || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Share text (English)
            </Typography>
            <Typography>{cause.shareTextEn || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Share text (Arabic)
            </Typography>
            <Typography>{cause.shareTextAr || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Donation email message
            </Typography>
            <Typography>{cause.donationEmailMessage || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Donation SMS message
            </Typography>
            <Typography>{cause.donationSmsMessage || 'Not set'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Donation Setup"
        description="Cause-level payment controls consumed by the mobile donation flow and APS native SDK intent."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Donations enabled
            </Typography>
            <Typography>{formatBoolean(cause.donationsEnabled)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Preferred payment channel
            </Typography>
            <Typography>
              {cause.preferredPaymentChannel === 'hosted_webview'
                ? 'Hosted web payment'
                : 'APS mobile SDK'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              APS environment
            </Typography>
            <Typography>{cause.apsEnvironment || 'Backend default'}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              APS command
            </Typography>
            <Typography>{cause.apsCommand || 'Backend default'}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              APS payment option
            </Typography>
            <Typography>{cause.apsPaymentOption || 'Not set'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Flags & Audit"
        description="Operational flags, color settings, and record ownership details."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Published
            </Typography>
            <Typography>{formatBoolean(cause.published)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Shareable
            </Typography>
            <Typography>{formatBoolean(cause.shareable)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Is special
            </Typography>
            <Typography>{formatBoolean(cause.isSpecial)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Zakat eligible
            </Typography>
            <Typography>{formatBoolean(cause.isZakatEligible)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Itemization
            </Typography>
            <Typography>{formatBoolean(cause.isItemization)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Primary color
            </Typography>
            <Typography>{cause.colorPrimary || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Secondary color
            </Typography>
            <Typography>{cause.colorSecondary || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Third color
            </Typography>
            <Typography>{cause.colorThird || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Created by ID
            </Typography>
            <Typography>{cause.createdById || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Updated by ID
            </Typography>
            <Typography>{cause.updatedById || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Created at
            </Typography>
            <Typography>{cause.createdAt || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Updated at
            </Typography>
            <Typography>{cause.updatedAt || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Deleted at
            </Typography>
            <Typography>{cause.deletedAt || 'Not set'}</Typography>
          </Grid>
        </Grid>
      </SectionCard>
    </AdminPage>
  );
};

export default CauseDetailsPage;
